import { LoadingService } from '@/core/services/loading.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CurrentUser } from '@/core/models/user';
import { UserService } from '@/core/services/open-api/user.service';
import { AppComponent } from '@/app.component';
import { CrmWebhookEvent, CrmWebhookService } from '@/core/services/crm-webhook.service';
import { DefaultDateFormatListService } from '@/core/services/default-date-format-list.service';
import { AngularTranslations, SelectOption } from '@/core/types/angular-primeng-api';
import { ThemeMode } from '@/core/services/theme.service';
import { combineLatest, merge, of, shareReplay, Subject, switchMap, takeUntil } from 'rxjs';
import { SharedFunctions } from '@/core/utils/shared-functions';
import { ToastService } from '@/core/services/toast.service';
import { TimeFormat } from '@/core/models/time-format';
import { UserUpdateForm } from '@/core/models/account';
import { UserSettings } from '@/core/models/user-settings';
import { UserUpdateErrorService } from '@/core/services/user-update-error.service';

type ProfileSection = 'name' | 'email' | 'language' | 'theme' | 'formats';

type ProfileUserField = 'firstName' | 'lastName' | 'email';

type ProfileSettingsField = 'language' | 'color' | 'dateFormat' | 'timeFormat';

/**
 * Defines which form edits belong to each profile Save button. For example, saving 'theme' applies
 * the color selection while pending name, email and language edits remain unsaved.
 * `user` fields belong directly to CurrentUser; `settings` fields belong to CurrentUser.settings.
 * saveProfile() uses this mapping to select edits, and applySavedSection() uses it to refresh only
 * that section's inputs from the server response, preserving drafts in the other sections.
 */
const SECTION_FIELDS: Record<ProfileSection, { user: ProfileUserField[]; settings: ProfileSettingsField[] }> = {
    name: { user: ['firstName', 'lastName'], settings: [] },
    email: { user: ['email'], settings: [] },
    language: { user: [], settings: ['language'] },
    theme: { user: [], settings: ['color'] },
    formats: { user: [], settings: ['dateFormat', 'timeFormat'] },
};

/**
 * Collects changed profile values for a section's save request.
 * Omits unchanged values so legacy fields are not validated again (#747).
 *
 * @param fields - Fields belonging to the section being saved.
 * @param draft - Reads a field's current form value.
 * @param saved - Saved values to compare against.
 * @returns Changed field values, or an empty object if nothing changed.
 * Undefined values are omitted; edited empty strings are included.
 */
function getChangedProfileFields<T, K extends keyof T>(
    fields: K[],
    draft: (field: K) => T[K] | undefined,
    saved: T,
): Partial<Pick<T, K>> {
    const changes: Partial<Pick<T, K>> = {};
    for (const field of fields) {
        const value = draft(field);
        if (value !== undefined && value !== saved[field]) {
            changes[field] = value;
        }
    }
    return changes;
}

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false,
})
export class ProfileComponent implements OnInit, OnDestroy {
    firstName?: string;
    lastName?: string;
    email?: string;
    changePassword?: string;
    changePasswordRepeat?: string;
    // TODO: change bad practice of using 'any'
    selectedLanguageCode?: string;
    selectedColorCode?: ThemeMode;
    selectedDateFormatCode?: string;
    selectedTimeFormatCode?: TimeFormat;
    languages: SelectOption[] = [];
    colors: SelectOption[] = [];
    dateFormats: SelectOption[] = [];
    timeFormats: SelectOption<TimeFormat>[] = [];
    private me?: CurrentUser;
    private readonly destroy$ = new Subject<void>();

    constructor(
        public readonly app: AppComponent,
        private readonly crmWebhookService: CrmWebhookService,
        private userService: UserService,
        private readonly translate: TranslateService,
        private toastService: ToastService,
        private readonly router: Router,
        private readonly loadingService: LoadingService,
        private readonly userUpdateError: UserUpdateErrorService,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.loadingService.setLoading(true);

        this.dateFormats = DefaultDateFormatListService.getDateFormats();
        this.selectedDateFormatCode = this.dateFormats[0].code;

        const translationKeys: string[] = [
            'languages',
            'profile_color_light',
            'profile_color_dark',
            'profile_color_system',
        ];

        const translations$ = merge(
            of(null), // initial trigger
            this.translate.onLangChange,
        ).pipe(
            switchMap(() => this.translate.get(translationKeys)),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        combineLatest([translations$, this.userService.currentUser$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([translations, me]: [AngularTranslations, CurrentUser]) => {
                const previous = this.me;
                this.firstName = this.retainPendingEdit(this.firstName, previous?.firstName, me.firstName);
                this.lastName = this.retainPendingEdit(this.lastName, previous?.lastName, me.lastName);
                this.email = this.retainPendingEdit(this.email, previous?.email, me.email);

                this.languages = SharedFunctions.getLanguageOptions(this.translate);
                this.selectedLanguageCode = this.retainPendingEdit(
                    this.selectedLanguageCode,
                    previous?.settings.language,
                    this.languages.find((l) => l.code === me.settings.language)?.code,
                );

                this.colors = [
                    {
                        name: translations['profile_color_light'],
                        code: 'light',
                    },
                    {
                        name: translations['profile_color_dark'],
                        code: 'dark',
                    },
                    {
                        name: translations['profile_color_system'],
                        code: 'system',
                    },
                ];
                this.selectedColorCode = this.retainPendingEdit(
                    this.selectedColorCode,
                    previous?.settings.color,
                    me.settings.color,
                );

                this.timeFormats = DefaultDateFormatListService.getTranslatedTimeFormats(this.translate);
                this.selectedTimeFormatCode = this.retainPendingEdit(
                    this.selectedTimeFormatCode,
                    previous?.settings.timeFormat,
                    this.timeFormats.find((l) => l.code === me.settings.timeFormat)?.code,
                );

                // date format dropdown is not language-dependent, hence only a selection value update is needed
                this.selectedDateFormatCode = this.retainPendingEdit(
                    this.selectedDateFormatCode,
                    previous?.settings.dateFormat,
                    this.dateFormats.find((l) => l.code === me.settings.dateFormat)?.code,
                );

                this.me = me;
                this.loadingService.setLoading(false);
            });

        this.userService.loadCurrentUser(true).subscribe();
    }

    /**
     * Saves the changes made to a specific profile section.
     * @param section - The profile section being saved.
     * @returns void. The component's state is updated directly.
     */
    saveProfile(section: ProfileSection): void {
        const me = this.me;
        if (!me) {
            return;
        }
        const fields = SECTION_FIELDS[section];
        const changes: UserUpdateForm = getChangedProfileFields(fields.user, (field) => this[field], me);
        const settingsDraft = this.settingsDraft();
        const settingsChanges = getChangedProfileFields(fields.settings, (field) => settingsDraft[field], me.settings);
        if (Object.keys(settingsChanges).length > 0) {
            // Preserve cached settings, including report options, without resending names (#747).
            changes.settings = UserSettings.toJson(UserSettings.mergeUpdate(me.settings, settingsChanges));
        }
        if (Object.keys(changes).length === 0) {
            return; // nothing changed in this section, so there is nothing to save
        }
        const isMailChange = changes.email !== undefined;
        this.loadingService.setLoading(true);
        this.userService.patchCurrentUser(changes, undefined, isMailChange).subscribe({
            // calling patchCurrentUser() will update the currentUser$ observable and by this trigger an update of the component (see subscriptions in ngOnInit)
            next: (user) => {
                this.applySavedSection(section, user);
                // Note: email address change notification works via backend store sync, not CRM call (a CRM call would create a new CRM-user with the new email)
                if (!isMailChange) {
                    this.updateCRM(user);
                }
                this.loadingService.setLoading(false);
                this.app.setUserLanguage(user);
                this.toastService.success('standard_success', 'profile_profilesaved');
                this.app.setColorTheme(user);
                if (isMailChange) {
                    this.userService.initiateLogout();
                    void this.router.navigateByUrl('/logout');
                }
            },
            error: (error) => {
                this.userUpdateError.show(error, 'profile_profilesavingerror');
                this.loadingService.setLoading(false);
            },
        });
    }

    /**
     * Previously, saving any profile section also saved pending edits in other sections.
     * Each section now saves only its own fields. When the response updates the user cache,
     * preserve unsaved drafts in other sections instead of replacing them with incoming saved values.
     * This also applies to other cache refreshes. On the initial profile load, use the saved value.
     *
     * @typeParam T - The field's value type. Values are compared using strict equality.
     * @param draft - The current value in the form, which may contain an unsaved edit.
     * @param previous - The field's value from the previously cached user, if available.
     * @param saved - The field's value to apply from the incoming user update.
     * @returns The draft if a user was already loaded and the draft differs from the previous
     * value; otherwise, the incoming saved value.
     */
    private retainPendingEdit<T>(draft: T, previous: T | undefined, saved: T): T {
        return this.me && draft !== previous ? draft : saved;
    }

    /**
     * Maps dropdown values to settings field names (e.g. selectedColorCode to color) for getChangedProfileFields().
     * @returns A partial object mapping settings field names to their current draft values.
     */
    private settingsDraft(): Partial<Pick<UserSettings, ProfileSettingsField>> {
        return {
            language: this.selectedLanguageCode,
            color: this.selectedColorCode,
            dateFormat: this.selectedDateFormatCode,
            timeFormat: this.selectedTimeFormatCode,
        };
    }

    /**
     * Applies server-normalized values to the saved section while preserving other sections' unsaved inputs.
     * @param section - The profile section being updated.
     * @param user - The current user object containing the saved values.
     * @returns void. The component's state is updated directly.
     */
    private applySavedSection(section: ProfileSection, user: CurrentUser): void {
        const fields = SECTION_FIELDS[section];
        for (const field of fields.user) {
            this[field] = user[field];
        }
        const settings = fields.settings;
        if (settings.includes('language')) this.selectedLanguageCode = user.settings.language;
        if (settings.includes('color')) this.selectedColorCode = user.settings.color;
        if (settings.includes('dateFormat')) this.selectedDateFormatCode = user.settings.dateFormat;
        if (settings.includes('timeFormat')) this.selectedTimeFormatCode = user.settings.timeFormat;
    }

    updateCRM(user: CurrentUser | undefined = this.me): void {
        if (!user) return;
        void this.crmWebhookService.send(CrmWebhookEvent.updateProfile, {
            email: user.email,
            first_name: user.firstName,
            last_name: user.lastName,
            language: user.settings.language,
        });
    }

    saveNewPassword(): void {
        this.loadingService.setLoading(true);

        const passwordMatch = !!this.changePassword && this.changePassword === this.changePasswordRepeat;
        if (!passwordMatch) {
            this.toastService.error('standard_error', 'profile_passwordnomatch');
            this.loadingService.setLoading(false);
            return;
        }
        this.userService.patchCurrentUser({}, this.changePassword).subscribe({
            next: () => {
                this.loadingService.setLoading(false);
                this.toastService.success('standard_success', 'profile_passwordsaved');
            },
            error: (error) => {
                this.userUpdateError.show(error, 'standard_error');
                this.loadingService.setLoading(false);
            },
        });
    }
}
